import { useState, useEffect } from "react";
import { message } from "antd";
import {
  getDepartamentos,
  getMunicipios,
  getUnidadesMedida,
  getTributos,
  getCondicionesOperacion,
  getFormasPago,
  getCodigosActividadEconomica,
  getTiposDocumentoReceptor,
} from "../../../api/Catalogos/catalogos.api";

export interface CatalogOptions {
  departamentos: Array<{ value: string; label: string; id: number }>;
  municipios: Array<{
    value: string;
    label: string;
    departamento_id: number;
    id: number;
  }>;
  unidadesMedida: Array<{ value: number; label: string }>;
  tributos: Array<{ value: string; label: string }>;
  condicionesOperacion: Array<{ value: number; label: string }>;
  formasPago: Array<{ value: string; label: string }>;
  actividadesEconomicas: Array<{ value: string; label: string }>;
  tiposDocumentoReceptor: Array<{ value: string; label: string }>;
}

export const useCatalogos = () => {
  const [catalogos, setCatalogos] = useState<CatalogOptions>({
    departamentos: [],
    municipios: [],
    unidadesMedida: [],
    tributos: [],
    condicionesOperacion: [],
    formasPago: [],
    actividadesEconomicas: [],
    tiposDocumentoReceptor: [],
  });
  const [loading, setLoading] = useState(false);

  const loadCatalogos = async () => {
    try {
      setLoading(true);

      const [
        departamentosData,
        municipiosData,
        unidadesMedidaData,
        tributosData,
        condicionesOperacionData,
        formasPagoData,
        actividadesEconomicasData,
        tiposDocumentoReceptorData,
      ] = await Promise.all([
        getDepartamentos(),
        getMunicipios(),
        getUnidadesMedida(),
        getTributos(),
        getCondicionesOperacion(),
        getFormasPago(),
        getCodigosActividadEconomica(),
        getTiposDocumentoReceptor(),
      ]);

      // Formatear departamentos
      const departamentosFormatted = departamentosData.map((dept) => ({
        value: dept.codigo,
        label: dept.nombre,
        id: dept.id,
      }));

      // Formatear municipios - CORREGIDO para incluir departamento_id
      const municipiosFormatted = municipiosData.map((mun) => ({
        value: mun.codigo,
        label: mun.nombre,
        departamento_id: mun.departamento_id,
        id: mun.id,
      }));

      // Formatear unidades de medida
      const unidadesMedidaFormatted = unidadesMedidaData.values.map(
        (unidad) => ({
          value: parseInt(unidad.code),
          label: unidad.value,
        })
      );

      // Formatear tributos
      const tributosFormatted = tributosData.values.map((tributo) => ({
        value: tributo.code,
        label: tributo.value,
      }));

      // Formatear condiciones de operación
      const condicionesOperacionFormatted = condicionesOperacionData.values.map(
        (condicion) => ({
          value: parseInt(condicion.code),
          label: condicion.value,
        })
      );

      // Formatear formas de pago
      const formasPagoFormatted = formasPagoData.values.map((forma) => ({
        value: forma.code,
        label: forma.value,
      }));

      // Formatear actividades económicas
      const actividadesEconomicasFormatted =
        actividadesEconomicasData.values.map((actividad) => ({
          value: actividad.code,
          label: `${actividad.code} - ${actividad.value}`,
        }));

      // Formatear tipos de documento de receptor
      const tiposDocumentoReceptorFormatted =
        tiposDocumentoReceptorData.values.map((tipo) => ({
          value: tipo.code,
          label: tipo.value,
        }));

      setCatalogos({
        departamentos: departamentosFormatted,
        municipios: municipiosFormatted,
        unidadesMedida: unidadesMedidaFormatted,
        tributos: tributosFormatted,
        condicionesOperacion: condicionesOperacionFormatted,
        formasPago: formasPagoFormatted,
        actividadesEconomicas: actividadesEconomicasFormatted,
        tiposDocumentoReceptor: tiposDocumentoReceptorFormatted,
      });
    } catch (error) {
      console.error("Error loading catalogs:", error);
      message.error("Error al cargar los catálogos");
    } finally {
      setLoading(false);
    }
  };

  const getMunicipiosByDepartamento = (
    departamentoIdOrCode: number | string
  ) => {
    let departamentoId: number;

    // Si es string, buscar el departamento por código para obtener el ID
    if (typeof departamentoIdOrCode === "string") {
      const departamento = catalogos.departamentos.find(
        (dept) => dept.value === departamentoIdOrCode
      );
      if (!departamento) {
        return [];
      }
      departamentoId = departamento.id;
    } else {
      departamentoId = departamentoIdOrCode;
    }

    return catalogos.municipios.filter(
      (mun) => mun.departamento_id === departamentoId
    );
  };

  useEffect(() => {
    loadCatalogos();
  }, []);

  return {
    catalogos,
    loading,
    getMunicipiosByDepartamento,
    reloadCatalogos: loadCatalogos,
  };
};
